import {
  type ChatConfigProvider,
  INITIAL_VOICE_MACHINE,
  logger,
  type Theme,
  type ToolCallEvent,
  type ToolCallHandler,
  type TriggerButtonConfig,
  type UserIdentity,
  type VoiceMachine,
  type WidgetMode,
  YakEmbed,
} from "@yak-io/javascript";

// ── Types ───────────────────────────────────────────────────────────────────

export type ToolCallEventHandler = (event: ToolCallEvent) => void;

export type YakProviderOptions = {
  appId: string;
  mode?: WidgetMode;
  getConfig?: ChatConfigProvider;
  onToolCall?: ToolCallHandler;
  theme?: Theme;
  onRedirect?: (path: string) => void;
  disableRestartButton?: boolean;
  /**
   * Privacy opt-out: stop sending any page context (URL, title, visible text)
   * to the assistant. The widget still works; it just won't be aware of the
   * current page.
   */
  disablePageContent?: boolean;
  trigger?: boolean | TriggerButtonConfig;
  /**
   * Signed end-user identity. When supplied, the widget persists conversations
   * server-side keyed to this user and surfaces a history pane. The `hash` must
   * be HMAC-SHA256(apiSecret, id) computed on the integrator's backend — never
   * expose `apiSecret` to the browser. Omit for anonymous mode (no persistence).
   * Use `setUser()` to change it after setup (login/logout).
   */
  user?: UserIdentity;
};

export type YakState = {
  isOpen: boolean;
  isReady: boolean;
  chatLoading: boolean;
  voiceMachine: VoiceMachine;
  voiceLoading: boolean;
};

/** Handle for controlling the Yak widget — chat + voice — from Angular. */
export type YakApi = {
  /** Whether the chat panel is currently open. */
  readonly isOpen: boolean;
  /** Whether the chat iframe is ready to receive messages. */
  readonly isReady: boolean;
  /** Whether the chat is opening but not yet interactive (`isOpen && !isReady`). */
  readonly chatLoading: boolean;
  /** Current voice state-machine snapshot. `idle` when mode is `chat`. */
  readonly voiceMachine: VoiceMachine;
  /** Whether the voice session is establishing its connection (`state === "connecting"`). */
  readonly voiceLoading: boolean;
  /** Open the chat panel. */
  open: () => void;
  /** Close the chat panel. */
  close: () => void;
  /** Open the chat panel and send a specific prompt. */
  openWithPrompt: (prompt: string) => void;
  /** Start a voice session. Must be invoked from a user gesture. */
  voiceStart: () => Promise<void>;
  /** Stop the current voice session. */
  voiceStop: () => Promise<void>;
  /** Toggle voice: start if idle/error, stop if active. */
  voiceToggle: () => Promise<void>;
  /**
   * Set or clear the signed end-user identity after setup. Call on login
   * (`setUser({ id, hash })`) and logout (`setUser(undefined)`). Switching to a
   * different id or logging out rotates the stored session token; the first
   * login after anonymous use keeps it so pre-login history can be claimed.
   */
  setUser: (user?: UserIdentity) => void;
  /** Subscribe to tool-call completion events; returns an unsubscribe function. */
  subscribeToToolEvents: (handler: ToolCallEventHandler) => () => void;
  /** Subscribe to chat + voice state changes. */
  subscribeToState: (handler: (state: YakState) => void) => () => void;
  /** Mount the widget into the DOM. */
  mount: () => void;
  /** Tear down the widget and remove its listeners. */
  destroy: () => void;
};

// ── Provider factory ────────────────────────────────────────────────────────

/**
 * Creates a yak widget (chat + voice) for Angular.
 * Call `mount()` in `ngOnInit` and `destroy()` in `ngOnDestroy`.
 */
export function createYakProvider(options: YakProviderOptions): YakApi {
  let _isOpen = false;
  let _isReady = false;
  let _voiceMachine: VoiceMachine = INITIAL_VOICE_MACHINE;
  const toolEventSubscribers = new Set<ToolCallEventHandler>();
  const stateSubscribers = new Set<(state: YakState) => void>();

  const notifyState = () => {
    const snap: YakState = {
      isOpen: _isOpen,
      isReady: _isReady,
      chatLoading: _isOpen && !_isReady,
      voiceMachine: _voiceMachine,
      voiceLoading: _voiceMachine.state === "connecting",
    };
    for (const subscriber of stateSubscribers) {
      try {
        subscriber(snap);
      } catch (err) {
        logger.warn("Error in state subscriber:", err);
      }
    }
  };

  const handleToolCallComplete = (event: ToolCallEvent) => {
    logger.debug("Tool call completed, notifying subscribers:", {
      name: event.name,
      ok: event.ok,
      subscriberCount: toolEventSubscribers.size,
    });
    for (const handler of toolEventSubscribers) {
      try {
        handler(event);
      } catch (err) {
        logger.warn("Error in tool event subscriber:", err);
      }
    }
  };

  const resolvedRedirect =
    options.onRedirect ??
    (typeof window !== "undefined" ? (path: string) => window.location.assign(path) : undefined);

  const embed = new YakEmbed({
    appId: options.appId,
    mode: options.mode,
    theme: options.theme,
    trigger: options.trigger ?? false,
    getConfig: options.getConfig,
    onToolCall: options.onToolCall,
    onRedirect: resolvedRedirect,
    options: {
      disableRestartButton: options.disableRestartButton,
      disablePageContent: options.disablePageContent,
    },
    onToolCallComplete: handleToolCallComplete,
    user: options.user,
  });

  embed.onStateChange((state) => {
    _isOpen = state.isOpen;
    _isReady = state.isReady;
    notifyState();
  });
  embed.onVoiceStateChange((m) => {
    _voiceMachine = m;
    notifyState();
  });

  // Fetch chat config on first open
  if (options.getConfig) {
    const getConfig = options.getConfig;
    let configFetched = false;

    embed.onStateChange((state) => {
      if (state.isOpen && !configFetched) {
        configFetched = true;
        logger.debug("Getting chat config");

        Promise.resolve(getConfig())
          .then((config) => {
            logger.debug(
              `Chat config loaded with ${config.tools?.tools.length ?? 0} tools and ${config.routes?.routes.length ?? 0} routes`
            );
            embed.getClient().updateConfig({ chatConfig: config });
          })
          .catch((err) => {
            logger.warn("Error getting chat config:", err);
            configFetched = false;
          });
      }
    });
  }

  const voiceStart = async () => {
    try {
      await embed.voiceStart();
    } catch (err) {
      logger.warn("Voice start failed", err);
    }
  };

  return {
    get isOpen() {
      return _isOpen;
    },
    get isReady() {
      return _isReady;
    },
    get chatLoading() {
      return _isOpen && !_isReady;
    },
    get voiceMachine() {
      return _voiceMachine;
    },
    get voiceLoading() {
      return _voiceMachine.state === "connecting";
    },
    open: () => embed.open(),
    close: () => embed.close(),
    openWithPrompt: (prompt: string) => embed.openWithPrompt(prompt),
    voiceStart,
    voiceStop: () => embed.voiceStop(),
    voiceToggle: () => embed.voiceToggle(),
    setUser: (user?: UserIdentity) => {
      // Core clears/rotates the session token when the id changes; we only forward.
      embed.getClient().updateConfig({ user });
      embed.getVoiceSession()?.updateConfig({ user });
    },
    subscribeToToolEvents: (handler: ToolCallEventHandler) => {
      toolEventSubscribers.add(handler);
      return () => {
        toolEventSubscribers.delete(handler);
      };
    },
    subscribeToState: (handler: (state: YakState) => void) => {
      stateSubscribers.add(handler);
      return () => {
        stateSubscribers.delete(handler);
      };
    },
    mount: () => embed.mount(),
    destroy: () => embed.destroy(),
  };
}
